import type {
    Cents,
    FormattedDollars,
} from '@fruitful/member-profile/src/common/types/Currency';

import { isEmpty } from './common';

const NOT_CURRENCY_CHARACTERS_REGEX = /[^-?\d+(\\.\d{2})?]/g;
const THOUSANDS_PLACE_COMMAS_REGEX = /\B(?=(\d{3})+(?!\d))/g;
const NO_LEADING_ZEROS_REGEX = /^0+(?!$)/;
const DECIMAL_POINT_REGEX = /\./g;
const FORMATTED_CURRENCY_REGEX =
    /^(\$?[0-9])(\d{0,2}(?:,\d{3})*(?:\.\d{0,2})?)$/;
const DOLLAR_SIGN_WITH_SPACE = '$ ';

export const roundToTwoDecimals = (value: number): number => {
    return Math.round(value * 100) / 100;
};

export const centsToDollars = (cents: number): number => {
    return roundToTwoDecimals(cents / 100);
};

export const dollarsToCents = (dollars: number): number => {
    return Math.round(dollars * 100);
};

export const roundCentsToWholeDollarCents = (cents: number): number => {
    return Math.round(cents / 100) * 100;
};

export const formatDollars = (dollarValue = 0, showDecimals = true): string => {
    const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        ...(showDecimals
            ? {}
            : { maximumFractionDigits: 0, minimumFractionDigits: 0 }),
    });
    return formatter.format(dollarValue);
};

export const centsToFormattedDollars = (
    value: number,
    showDecimals = true,
): string => {
    return formatDollars(centsToDollars(value), showDecimals);
};

export const formattedDollarsToCents = (
    formattedDollars: FormattedDollars,
): Cents => {
    // Remove anything other than digits and decimal points (i.e. strip out any currency formatting)
    const unformattedDollars = formattedDollars.replace(
        NOT_CURRENCY_CHARACTERS_REGEX,
        '',
    );

    return dollarsToCents(parseFloat(unformattedDollars));
};

/**
 * Rounds a cent value up to the nearest $100 (10,000 cents).
 * @param cents The value in cents to round up.
 * @returns The value rounded up to the nearest $100 (10,000 cents).
 */
export const roundCentsUpToNearestHundredDollars = (cents: number): number => {
    return Math.ceil(cents / 10000) * 10000;
};

/**
 * Rounds a cent value up to the nearest $1,000 (100,000 cents).
 * @param cents The value in cents to round up.
 * @returns The value rounded up to the nearest $1,000 (100,000 cents).
 */
export const roundCentsUpToNearestThousandDollars = (cents: number): number => {
    return Math.ceil(cents / 100000) * 100000;
};

const baseFormatCurrencyInputOnChange = (
    previousValue: string,
    nextValue: string,
    excludeDecimals = false,
): string => {
    // Remove anything other than digits and decimal points (i.e. strip out any currency formatting)
    let working = nextValue.replace(NOT_CURRENCY_CHARACTERS_REGEX, '');

    if (isEmpty(working)) {
        return DOLLAR_SIGN_WITH_SPACE;
    }

    if (excludeDecimals) {
        working = working.replace(DECIMAL_POINT_REGEX, '');
    }

    // Remove leading zeros unless the value is just '0' (e.g. '00012' becomes '12', but '0000' becomes '0')
    working = working.replace(NO_LEADING_ZEROS_REGEX, '');

    // Add in commas every thousand's place
    const formattedValue = working.replace(THOUSANDS_PLACE_COMMAS_REGEX, ',');

    // If we don't have a valid formatted number, return the previous value
    if (FORMATTED_CURRENCY_REGEX.exec(formattedValue)) {
        return `${DOLLAR_SIGN_WITH_SPACE}${formattedValue}`;
    }
    return previousValue;
};

export const formatCurrencyInputOnChange = (
    previousValue: string,
    nextValue: string,
): string => baseFormatCurrencyInputOnChange(previousValue, nextValue);

export const formatCurrencyInputOnChangeWithoutDecimals = (
    previousValue: string,
    nextValue: string,
): string => baseFormatCurrencyInputOnChange(previousValue, nextValue, true);
